import AppErrorFallback from '@/common/components/error-state/AppErrorFallback.tsx';
import LightListFeaturePage from '@/common/components/list-route/LightListFeaturePage.tsx';
import NotificationSplitView from '@/common/notifications/NotificationSplitView.tsx';
import {
  getNotificationsControllerGetNotificationTypesQueryOptions,
  getNotificationsControllerGetNotificationsQueryOptions,
} from '@/generated/api/notifications.ts';
import type {
  NotificationTypeDto,
  NotificationsControllerGetNotificationsParams,
} from '@/generated/api/pacApi.schemas.ts';
import { getQueryErrorEventId } from '@/main.tsx';
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  MultiSelect,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import { XIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/me/notifications/all')({
  component: RouteComponent,
});

const subCategorySeparator = '\u001f';

function RouteComponent() {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(
    [],
  );

  const notificationTypesQuery = useQuery(
    getNotificationsControllerGetNotificationTypesQueryOptions({
      query: { staleTime: 'static' },
    }),
  );

  const notificationTypes =
    notificationTypesQuery.data?.notificationTypes ?? [];

  const categoryOptions = useMemo(
    () =>
      uniqueSorted(notificationTypes.map((type) => type.category)).map(
        (category) => ({
          value: category,
          label: category,
        }),
      ),
    [notificationTypes],
  );

  const subCategoryOptions = useMemo(() => {
    const selectedCategorySet = new Set(selectedCategories);
    const visibleTypes = notificationTypes.filter(
      (type) =>
        selectedCategorySet.size === 0 || selectedCategorySet.has(type.category),
    );

    return uniqueSortedBy(
      visibleTypes.map((type) => ({
        value: getSubCategoryValue(type),
        label:
          selectedCategorySet.size === 0
            ? `${type.category} / ${type.subCategory}`
            : type.subCategory,
      })),
      (item) => item.value,
    );
  }, [notificationTypes, selectedCategories]);

  useEffect(() => {
    const availableValues = new Set(
      subCategoryOptions.map((option) => option.value),
    );

    setSelectedSubCategories((current) => {
      const next = current.filter((value) => availableValues.has(value));
      return next.length === current.length ? current : next;
    });
  }, [subCategoryOptions]);

  const filtersActive =
    selectedCategories.length > 0 || selectedSubCategories.length > 0;

  const selectedNotificationKeys = useMemo(() => {
    if (!filtersActive) return [];

    const selectedCategorySet = new Set(selectedCategories);
    const selectedSubCategorySet = new Set(selectedSubCategories);

    return notificationTypes
      .filter((type) => {
        const matchesCategory =
          selectedCategorySet.size === 0 ||
          selectedCategorySet.has(type.category);
        const matchesSubCategory =
          selectedSubCategorySet.size === 0 ||
          selectedSubCategorySet.has(getSubCategoryValue(type));

        return matchesCategory && matchesSubCategory;
      })
      .map((type) => type.key);
  }, [
    filtersActive,
    notificationTypes,
    selectedCategories,
    selectedSubCategories,
  ]);

  const notificationParams = filtersActive
    ? ({
        key: selectedNotificationKeys,
      } satisfies NotificationsControllerGetNotificationsParams)
    : undefined;
  const notificationsEnabled =
    !filtersActive || selectedNotificationKeys.length > 0;

  const notificationsQueryOptions =
    getNotificationsControllerGetNotificationsQueryOptions(notificationParams, {
      query: {
        enabled: notificationsEnabled,
      },
    });
  const notificationsQuery = useQuery(notificationsQueryOptions);

  if (notificationsQuery.isError) {
    return (
      <LightListFeaturePage title="Notifications">
        <AppErrorFallback
          title="Failed to load notifications"
          message="We couldn't load notifications right now. Try again or report this issue."
          eventId={getQueryErrorEventId(notificationsQueryOptions.queryKey)}
          onRetry={() => notificationsQuery.refetch()}
          onReload={() => window.location.reload()}
          onBack={() => window.history.back()}
        />
      </LightListFeaturePage>
    );
  }

  const notifications = notificationsEnabled
    ? (notificationsQuery.data?.notifications ?? [])
    : [];
  const totalCount = notificationsEnabled
    ? (notificationsQuery.data?.totalCount ?? 0)
    : 0;
  const unreadCount = notificationsEnabled
    ? (notificationsQuery.data?.unreadCount ?? 0)
    : 0;

  return (
    <LightListFeaturePage
      title="Notifications"
      subtitle="Review in-app notifications by type."
      action={
        notificationsQuery.data ? (
          <Group gap="xs" wrap="nowrap">
            <Badge variant="light" color={unreadCount > 0 ? 'blue' : 'gray'}>
              {unreadCount > 0 ? `${unreadCount} unread` : 'All read'}
            </Badge>
            <Badge variant="outline" color="gray">
              {totalCount} total
            </Badge>
          </Group>
        ) : null
      }
    >
      <Stack h="100%" gap="sm" style={{ minHeight: 0 }}>
        <Box
          p="sm"
          style={{
            flexShrink: 0,
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 6,
          }}
        >
          <Stack gap="xs">
            <Group align="flex-end" gap="sm" wrap="wrap">
              <MultiSelect
                label="Category"
                placeholder="All categories"
                data={categoryOptions}
                value={selectedCategories}
                onChange={setSelectedCategories}
                disabled={notificationTypesQuery.isLoading}
                searchable
                clearable
                hidePickedOptions
                style={{ flex: '1 1 260px' }}
              />
              <MultiSelect
                label="Subcategory"
                placeholder="All subcategories"
                data={subCategoryOptions}
                value={selectedSubCategories}
                onChange={setSelectedSubCategories}
                disabled={
                  notificationTypesQuery.isLoading ||
                  subCategoryOptions.length === 0
                }
                searchable
                clearable
                hidePickedOptions
                style={{ flex: '1 1 320px' }}
              />
              <Button
                variant="default"
                leftSection={<XIcon size={14} />}
                disabled={!filtersActive}
                onClick={() => {
                  setSelectedCategories([]);
                  setSelectedSubCategories([]);
                }}
              >
                Clear
              </Button>
            </Group>

            {notificationTypesQuery.isError ? (
              <Alert color="red" title="Filters failed to load">
                <Text size="sm">
                  Notifications are still available, but category filters could
                  not be loaded.
                </Text>
              </Alert>
            ) : null}
          </Stack>
        </Box>

        <Box
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 6,
          }}
        >
          {notificationsEnabled && notificationsQuery.isLoading ? (
            <NotificationsLoadingState />
          ) : (
            <NotificationSplitView
              notifications={notifications}
              height="100%"
              listWidth={360}
              autoSelectFirst
              emptyLabel={
                filtersActive
                  ? 'No notifications match these filters.'
                  : 'No notifications yet.'
              }
            />
          )}
        </Box>
      </Stack>
    </LightListFeaturePage>
  );
}

function NotificationsLoadingState() {
  return (
    <Group h="100%" align="stretch" gap={0} wrap="nowrap">
      <Box
        w={360}
        p="sm"
        style={{
          borderRight: '1px solid var(--mantine-color-default-border)',
        }}
      >
        <Stack gap="sm">
          {Array.from({ length: 8 }).map((_, index) => (
            <Box key={index}>
              <Skeleton height={14} width="76%" mb={8} />
              <Skeleton height={10} width="92%" mb={6} />
              <Skeleton height={10} width="38%" />
            </Box>
          ))}
        </Stack>
      </Box>

      <Box p="md" style={{ flex: 1 }}>
        <Skeleton height={18} width={180} mb="md" />
        <Skeleton height={24} width="64%" mb="lg" />
        <Skeleton height={12} width="96%" mb="xs" />
        <Skeleton height={12} width="88%" mb="xs" />
        <Skeleton height={12} width="92%" mb="xs" />
        <Skeleton height={12} width="54%" />
      </Box>
    </Group>
  );
}

function getSubCategoryValue(type: NotificationTypeDto) {
  return `${type.category}${subCategorySeparator}${type.subCategory}`;
}

function uniqueSorted(values: string[]) {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

function uniqueSortedBy<T>(values: T[], getKey: (value: T) => string) {
  const result = new Map<string, T>();

  for (const value of values) {
    result.set(getKey(value), value);
  }

  return Array.from(result.values()).sort((a, b) =>
    getKey(a).localeCompare(getKey(b)),
  );
}
